import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '../../components/common/Modal';
import { proofRevisionSchema, ProofRevisionSchema } from './proofFeedbackSchema';
import { ProofRevisionInput, REVISION_ITEM_OPTIONS } from './proof';
import { ProofRevisionInfo } from '../../services/proofService';
import styles from './ProofForms.module.scss';

interface ProofRevisionModalProps {
  isOpen: boolean;
  revision: ProofRevisionInfo;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: ProofRevisionInput) => Promise<boolean>;
}

// 시안 수정요청 전용 양식 팝업. 제출 1건이 수정 1회로 차감되며, 무료 횟수를 넘으면 추가금 동의가 필요하다
function ProofRevisionModal({ isOpen, revision, error, onClose, onSubmit }: ProofRevisionModalProps) {
  const remaining = Math.max(0, revision.freeLimit - revision.used);
  const isPaid = remaining === 0;
  const nextRound = revision.used + 1;

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProofRevisionSchema>({
    resolver: zodResolver(proofRevisionSchema),
    defaultValues: { items: [], message: '', agreeExtraFee: false },
  });

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const selectedItems = watch('items') ?? [];

  const submit = async (values: ProofRevisionSchema) => {
    if (isPaid && !values.agreeExtraFee) {
      setError('agreeExtraFee', { message: '추가 비용에 동의해야 수정요청을 보낼 수 있습니다.' });
      return;
    }
    if (await onSubmit(values)) reset();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form className={styles.modal} onSubmit={handleSubmit(submit)} aria-labelledby="revisionModalTitle">
        <div className={styles.modalHead}>
          <h2 id="revisionModalTitle">시안 수정요청</h2>
          <button type="button" className={styles.close} aria-label="닫기" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* 남은 횟수: 가장 먼저 눈에 띄게 */}
        <div className={`${styles.countBox} ${isPaid ? styles.countPaid : ''}`}>
          {isPaid ? (
            <>
              <strong>무료 수정 {revision.freeLimit}회를 모두 사용했습니다</strong>
              <span>
                이번 요청({nextRound}회차)부터는 유료로 진행되며 <b>{revision.extraFee.toLocaleString()}원</b>이 결제금액에
                추가됩니다.
              </span>
            </>
          ) : (
            <>
              <strong>
                남은 무료 수정 <b>{remaining}회</b> / {revision.freeLimit}회
              </strong>
              <span>
                이번 요청은 {nextRound}회차 수정으로 접수되며, 접수 후 남은 무료 횟수는 {remaining - 1}회입니다.
              </span>
            </>
          )}
        </div>

        <fieldset className={styles.field}>
          <legend>
            수정 항목 <em>(복수 선택)</em>
          </legend>
          <div className={styles.itemPills}>
            {REVISION_ITEM_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`${styles.itemPill} ${selectedItems.includes(opt.value) ? styles.itemPillOn : ''}`}
              >
                <input type="checkbox" value={opt.value} {...register('items')} />
                {opt.label}
              </label>
            ))}
          </div>
          {errors.items && <p className={styles.error}>{errors.items.message}</p>}
        </fieldset>

        <div className={styles.field}>
          <label htmlFor="revisionMessage">상세 수정 내용</label>
          <textarea
            id="revisionMessage"
            rows={6}
            placeholder={'수정할 위치와 내용을 번호를 붙여 구체적으로 적어주세요.\n예) 1. 상단 로고 크기를 지금보다 20% 크게\n     2. 뒷면 전화번호 053-000-0000 으로 변경'}
            {...register('message')}
          />
          {errors.message && <p className={styles.error}>{errors.message.message}</p>}
        </div>

        <div className={styles.field}>
          <label htmlFor="revisionAttachment">참고 이미지 (선택)</label>
          <input id="revisionAttachment" type="file" accept="image/*" {...register('attachment')} />
        </div>

        <ul className={styles.guide}>
          <li>수정요청 1건이 수정 1회로 차감됩니다. 수정할 내용은 한 번에 모두 적어주세요.</li>
          <li>무료 수정은 {revision.freeLimit}회까지이며, 이후 추가 수정은 1회당 비용이 결제금액에 합산됩니다.</li>
          <li>단순 문의는 수정 횟수가 차감되지 않는 &lsquo;쪽지&rsquo;를 이용해주세요.</li>
        </ul>

        {isPaid && (
          <div className={styles.feeAgreement}>
            <label>
              <input type="checkbox" {...register('agreeExtraFee')} />
              추가 수정 비용 {revision.extraFee.toLocaleString()}원이 결제금액에 추가되는 것에 동의합니다.
            </label>
            {errors.agreeExtraFee && <p className={styles.error}>{errors.agreeExtraFee.message}</p>}
          </div>
        )}

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.modalActions}>
          <button type="button" className={styles.cancel} onClick={onClose}>
            취소
          </button>
          <button type="submit" className={styles.submit} disabled={isSubmitting}>
            {isSubmitting
              ? '접수 중...'
              : isPaid
                ? `유료 수정요청 보내기 (+${revision.extraFee.toLocaleString()}원)`
                : `${nextRound}회차 수정요청 보내기`}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default ProofRevisionModal;
